import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { mget, mpost, mput, mpatch, errMsg, eur, fmtDate } from "@/manager/lib";
import { PageHeader, Card, Field, Pill, Empty, goldBtn } from "@/manager/ui";

const ORDER_STATUS = { pendiente: "bg-amber-100 text-amber-800", solicitado: "bg-sky-100 text-sky-800", confirmado: "bg-indigo-100 text-indigo-800", recibido: "bg-emerald-100 text-emerald-800", cancelado: "bg-slate-100 text-slate-500 line-through" };
const NEXT = { pendiente: ["solicitado", "cancelado"], solicitado: ["confirmado", "cancelado"], confirmado: ["recibido", "cancelado"] };
const S_BLANK = { name: "", category: "", phone: "", email: "", services: "", notes: "" };

function ShoppingList({ groups, onOrder }) {
  if (!groups.length) return <Card className="p-5"><Empty>Nada que reponer. Todo el stock está por encima del mínimo.</Empty></Card>;
  return groups.map((g) => (
    <Card key={g.supplier_id || "none"} className="p-5 mb-3" data-testid={`shop-group-${g.supplier_id || "none"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div><p className="font-display text-lg">{g.supplier_name}</p>{g.supplier_phone && <a href={`tel:${g.supplier_phone}`} className="text-xs flex items-center gap-1 text-[#9a7f3f]"><Phone size={12} /> {g.supplier_phone}</a>}</div>
        <Button className={goldBtn} onClick={() => onOrder(g)} data-testid={`shop-order-${g.supplier_id || "none"}`}>Crear pedido</Button>
      </div>
      {g.items.map((i) => <p key={i.product_id} className="text-sm flex justify-between py-1 border-t border-[#f0ece4]"><span>{i.name} <span className="text-xs text-[#6b6a66]">(quedan {i.quantity}, mín {i.min_stock})</span></span><span className="font-medium">Pedir {i.to_buy} {i.unit}</span></p>)}
    </Card>
  ));
}

export default function Purchases() {
  const [groups, setGroups] = useState([]);
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [sEdit, setSEdit] = useState(null);
  const load = useCallback(() => {
    mget("/inventory/shopping-list").then(setGroups); mget("/orders").then(setOrders); mget("/suppliers").then(setSuppliers);
  }, []);
  useEffect(() => { load(); }, [load]);

  const order = async (g) => {
    const lines = g.items.filter((i) => i.to_buy > 0).map((i) => ({ product_id: i.product_id, quantity: i.to_buy }));
    if (!lines.length) return toast.error("Define un stock objetivo mayor que el actual para calcular la cantidad.");
    try { await mpost("/orders", { supplier_id: g.supplier_id, lines }); toast.success("Pedido creado (pendiente). El stock subirá al recibirlo."); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const advance = async (o, status) => {
    try { await mpatch(`/orders/${o.id}`, { status }); toast.success(status === "recibido" ? "Pedido recibido: stock actualizado" : `Pedido ${status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const saveSupplier = async (e) => {
    e.preventDefault();
    try { if (sEdit.id) await mput(`/suppliers/${sEdit.id}`, sEdit); else await mpost("/suppliers", sEdit); setSEdit(null); load(); toast.success("Proveedor guardado"); } catch (err) { toast.error(errMsg(err)); }
  };
  const set = (k) => (e) => setSEdit({ ...sEdit, [k]: e.target.value });

  return (
    <div data-testid="manager-purchases">
      <PageHeader eyebrow="Compras" title="Reposición y proveedores">
        <Button className={goldBtn} onClick={() => setSEdit(S_BLANK)} data-testid="supplier-new"><Plus size={15} /> Proveedor</Button>
      </PageHeader>
      <h2 className="font-display text-xl mb-3">Lista de compra (bajo mínimo)</h2>
      <ShoppingList groups={groups} onOrder={order} />
      <h2 className="font-display text-xl mt-8 mb-3">Pedidos</h2>
      <Card className="divide-y divide-[#f0ece4]">
        {orders.map((o) => (
          <div key={o.id} className="px-5 py-3" data-testid={`order-row-${o.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <p className="flex-1 text-sm font-medium">{o.supplier_name} <span className="text-xs text-[#6b6a66]">· {fmtDate(o.created_at)} · {eur(o.total)}</span></p>
              <Pill className={ORDER_STATUS[o.status]} testid={`order-status-${o.id}`}>{o.status}</Pill>
              {(NEXT[o.status] || []).map((n) => <Button key={n} size="sm" variant={n === "cancelado" ? "ghost" : "outline"} className="rounded-full h-7 text-xs" onClick={() => advance(o, n)} data-testid={`order-${n}-${o.id}`}>{n === "recibido" ? "Marcar recibido" : n === "cancelado" ? "Cancelar" : `Marcar ${n}`}</Button>)}
            </div>
            <p className="text-xs text-[#6b6a66] mt-1">{o.lines.map((l) => `${l.quantity} ${l.unit} ${l.name}`).join(" · ")}</p>
          </div>
        ))}
        {!orders.length && <Empty>Sin pedidos todavía.</Empty>}
      </Card>
      <h2 className="font-display text-xl mt-8 mb-3">Proveedores</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {suppliers.map((s) => (
          <Card key={s.id} className="p-4" data-testid={`supplier-${s.id}`}>
            <div className="flex justify-between"><p className="font-medium">{s.name}</p><button onClick={() => setSEdit(s)} data-testid={`supplier-edit-${s.id}`}><Pencil size={14} /></button></div>
            <p className="text-xs text-[#6b6a66]">{s.category} · {s.services}</p>
            <p className="text-xs mt-1">{s.phone && <a href={`tel:${s.phone}`} className="underline mr-2">{s.phone}</a>}{s.email}</p>
          </Card>
        ))}
      </div>
      <Dialog open={!!sEdit} onOpenChange={(o) => !o && setSEdit(null)}>
        <DialogContent className="bg-white" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{sEdit?.id ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle></DialogHeader>
          {sEdit && (
            <form onSubmit={saveSupplier} className="grid grid-cols-2 gap-3">
              <Field label="Nombre *" className="col-span-2"><Input value={sEdit.name} onChange={set("name")} required data-testid="supplier-name" /></Field>
              <Field label="Categoría"><Input value={sEdit.category} onChange={set("category")} placeholder="Bebidas, catering, DJ…" data-testid="supplier-category" /></Field>
              <Field label="Teléfono"><Input value={sEdit.phone} onChange={set("phone")} data-testid="supplier-phone" /></Field>
              <Field label="Email" className="col-span-2"><Input value={sEdit.email} onChange={set("email")} data-testid="supplier-email" /></Field>
              <Field label="Productos / servicios" className="col-span-2"><Input value={sEdit.services} onChange={set("services")} data-testid="supplier-services" /></Field>
              <Field label="Condiciones / notas" className="col-span-2"><Input value={sEdit.notes} onChange={set("notes")} data-testid="supplier-notes" /></Field>
              <DialogFooter className="col-span-2"><Button type="submit" className={goldBtn} data-testid="supplier-save">Guardar</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
